import React, { useState, useEffect } from 'react';
import './TeamPart.css';

const TeamPart = () => {
  const [showTeamPaidModal, setShowTeamPaidModal] = useState(false);
  const [showTeamAceModal, setShowTeamAceModal] = useState(false);
  const [showTeamContactModal, setShowTeamContactModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmpassword: '',
    premiumType: '0',
    userCount: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [premiumType, setPremiumType] = useState('0');
  const [userCount, setUserCount] = useState('');
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [plans, setPlans] = useState([]);
  const [acePlans, setAcePlans] = useState([]);

  // Reset all form data to initial state
  const resetAllFormData = () => {
    setFormData({
      email: '',
      password: '',
      confirmpassword: '',
      premiumType: '0',
      userCount: '',
      message: '',
    });
    setFormErrors({});
    setError('');
    setSuccess('');
    setAmount('');
    setAmountError('');
    setPremiumType('0');
    setUserCount('');
    setLoading(false);
  };

  // Handler to open Teams Premium modal - closes other modals and resets data
  const handleOpenTeamPaidModal = () => {
    // Close other modals first
    setShowTeamAceModal(false);
    setShowTeamContactModal(false);
    // Reset all form data
    resetAllFormData();
    // Open this modal
    setShowTeamPaidModal(true);
  };

  // Handler to open Teams Ace modal - closes other modals and resets data
  const handleOpenTeamAceModal = () => {
    // Close other modals first
    setShowTeamPaidModal(false);
    setShowTeamContactModal(false);
    // Reset all form data
    resetAllFormData();
    // Open this modal
    setShowTeamAceModal(true);
  };

  // Handler to open Contact modal - closes other modals and resets data
  const handleOpenTeamContactModal = () => {
    // Close other modals first
    setShowTeamPaidModal(false);
    setShowTeamAceModal(false);
    // Reset all form data
    resetAllFormData();
    // Open this modal
    setShowTeamContactModal(true);
  };

  // Handler to close Teams Premium modal and reset form
  const handleCloseTeamPaidModal = () => {
    setShowTeamPaidModal(false);
    resetAllFormData();
  };

  // Handler to close Teams Ace modal and reset form
  const handleCloseTeamAceModal = () => {
    setShowTeamAceModal(false);
    resetAllFormData();
  };

  // Handler to close Contact modal and reset form
  const handleCloseTeamContactModal = () => {
    setShowTeamContactModal(false);
    resetAllFormData();
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const [teamRes, aceRes] = await Promise.all([
          fetch(process.env.REACT_APP_API_URL + '/api/v1/plans/team'),
          fetch(process.env.REACT_APP_API_URL + '/api/v1/plans/team-ace'),
        ]);

        if (!teamRes.ok) {
          throw new Error('Failed to fetch team plans');
        }
        if (!aceRes.ok) {
          throw new Error('Failed to fetch team ace plans');
        }

        const teamData = await teamRes.json();
        const aceData = await aceRes.json();

        // Log API responses to check structure
        console.log('Team Plans API Response:', teamData);
        console.log('Team Ace Plans API Response:', aceData);
        
        if (teamData?.status && Array.isArray(teamData?.data?.team_plans)) {
          console.log('Team Plans Data:', teamData.data.team_plans);
          // Log first plan to check structure
          if (teamData.data.team_plans.length > 0) {
            console.log('First Team Plan Structure:', teamData.data.team_plans[0]);
          }
          setPlans(teamData.data.team_plans);
        } else if (Array.isArray(teamData?.data?.team_plans)) {
          console.log('Team Plans Data (no status):', teamData.data.team_plans);
          setPlans(teamData.data.team_plans);
        } else {
          console.error('Expected team_plans array but got:', teamData);
          setPlans([]);
        }

        if (aceData?.status && Array.isArray(aceData?.data?.team_ace_plans)) {
          console.log('Team Ace Plans Data:', aceData.data.team_ace_plans);
          // Log first plan to check structure
          if (aceData.data.team_ace_plans.length > 0) {
            console.log('First Team Ace Plan Structure:', aceData.data.team_ace_plans[0]);
          }
          setAcePlans(aceData.data.team_ace_plans);
        } else if (Array.isArray(aceData?.data?.team_ace_plans)) {
          console.log('Team Ace Plans Data (no status):', aceData.data.team_ace_plans);
          setAcePlans(aceData.data.team_ace_plans);
        } else {
          console.error('Expected team_ace_plans array but got:', aceData);
          setAcePlans([]);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        setPlans([]);
        setAcePlans([]);
      }
    };

    fetchPlans();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log("API URL is:", process.env.REACT_APP_API_URL);
      const response = await fetch(process.env.REACT_APP_API_URL+'/api/v1/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          message: formData.message
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Handle new response format
      if (data.status) {
        setSuccess(data.message || 'Message sent successfully!');
        setFormData(prev => ({ ...prev, email: '', message: '' }));
      } else {
        setError(data.message || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Helper for email validation
  const isEmail = (email) => {
    var regex = /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
    return regex.test(email);
  };

  // Get max users from selected plan's num_of_workers field dynamically from API
  const getMaxUsersFromPlan = (premiumTypeValue, isAcePlan = false) => {
    if (!premiumTypeValue || premiumTypeValue === '0') {
      return null; // No plan selected
    }

    const planList = isAcePlan ? acePlans : plans;
    if (!planList || planList.length === 0) {
      console.warn('Plans not loaded yet');
      return null; // Plans not loaded
    }

    // premiumType format: "duration_type_amount" (e.g., "1_49.99")
    const [durationType, amount] = premiumTypeValue.split('_');
    
    // Find the matching plan
    const selectedPlan = planList.find(plan => 
      plan.duration_type === parseInt(durationType) && 
      parseFloat(plan.amount) === parseFloat(amount)
    );

    if (selectedPlan && selectedPlan.num_of_workers) {
      const maxUsers = parseInt(selectedPlan.num_of_workers);
      console.log('Max users from plan (dynamic):', maxUsers, 'Plan:', selectedPlan);
      return maxUsers;
    }

    // If plan found but num_of_workers is missing, log warning
    if (selectedPlan) {
      console.warn('Plan found but num_of_workers is missing:', selectedPlan);
    } else {
      console.warn('Plan not found for premiumType:', premiumTypeValue, 'Available plans:', planList);
    }

    return null; // Plan not found or num_of_workers missing
  };

  // Premium calculation logic
  const handlePremiumChange = (e) => {
    const value = e.target.value;
    setPremiumType(value);
    setFormData(prev => ({ ...prev, premiumType: value }));
    const isAcePlan = showTeamAceModal;
    const maxUsers = getMaxUsersFromPlan(value, isAcePlan);
    calculateAmount(value, userCount, maxUsers);
  };

  const handleUserCountChange = (e) => {
    const value = e.target.value;
    if (!/^\d*$/.test(value)) return;
    setUserCount(value);
    setFormData(prev => ({ ...prev, userCount: value }));
    const isAcePlan = showTeamAceModal;
    const maxUsers = getMaxUsersFromPlan(premiumType, isAcePlan);
    calculateAmount(premiumType, value, maxUsers);
  };

  const calculateAmount = (premium, users, maxUsers) => {
    setAmountError('');
    if (!premium || premium === '0' || !users) {
      setAmount('');
      return;
    }
    // Only validate if maxUsers is available from API
    if (maxUsers !== null && maxUsers !== undefined && parseInt(users) > maxUsers) {
      setUserCount('');
      setFormData(prev => ({ ...prev, userCount: '' }));
      setAmount('');
      setAmountError(`Can't add more than ${maxUsers} users`);
      return;
    }
    const price = premium.split('_')[1];
    if (!price || isNaN(price) || isNaN(users)) {
      setAmount('');
      return;
    }
    setAmount('Subscription Amount: $' + (Number(price) * Number(users)).toFixed(2));
  };

  // Handle input changes for modal form
  const handleModalInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'premiumType') handlePremiumChange(e);
    if (name === 'userCount') handleUserCountChange(e);
  };

  // Form validation and submit for Teams Premium
  const handleTeamPaidSubmit = async (e) => {
    e.preventDefault();
    let errors = {};
    if (!formData.email) {
      errors.email = 'Please Enter Email';
    } else if (!isEmail(formData.email)) {
      errors.email = 'Email not Valid';
    }
    if (!formData.password) {
      errors.password = 'Please Enter Password';
    } else if (!/^(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/.test(formData.password)) {
      errors.password = 'Please enter at least 1 Number, 1 Special Character and Minimum 8 Characters.';
    }
    if (!formData.confirmpassword) {
      errors.confirmpassword = 'Please Enter Confirm Password';
    } else if (formData.confirmpassword !== formData.password) {
      errors.confirmpassword = 'Confirm Password Should be Same as Password';
    }
    if (formData.premiumType === '0') {
      errors.premiumType = 'Please choose premium';
    }
    if (!formData.userCount) {
      errors.userCount = 'Please enter number of users';
    } else {
      // Get max users from selected plan dynamically from API
      const maxUsers = getMaxUsersFromPlan(formData.premiumType, false);
      if (maxUsers === null || maxUsers === undefined) {
        errors.userCount = 'Unable to validate user limit. Please refresh and try again.';
      } else if (parseInt(formData.userCount) > maxUsers) {
        errors.userCount = `Can't add more than ${maxUsers} users`;
      }
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(process.env.REACT_APP_API_URL+'/api/v1/team-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          premium_type_team_paid: formData.premiumType,
          number_of_user: formData.userCount,
          plan_type: 2, // Team Premium plan type
        }),
      });

      // Check if response is ok
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Handle new response format
      if (result.status && result.data) {
        if (result.data.status === 1 && result.data.checkout_id) {
          setSuccess(result.message || 'Registration successful! Redirecting to payment...');
          gotoCheckout(result.data.checkout_id);
        } else {
          setSuccess(result.message || 'Registration successful!');
        }
      } else {
        setError(result.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError(''+err.message||'');
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };


   // Form validation and submit for Teams Premium
  const handleTeamAceSubmit = async (e) => {
    e.preventDefault();
    let errors = {};
    if (!formData.email) {
      errors.email = 'Please Enter Email';
    } else if (!isEmail(formData.email)) {
      errors.email = 'Email not Valid';
    }
    if (!formData.password) {
      errors.password = 'Please Enter Password';
    } else if (!/^(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/.test(formData.password)) {
      errors.password = 'Please enter at least 1 Number, 1 Special Character and Minimum 8 Characters.';
    }
    if (!formData.confirmpassword) {
      errors.confirmpassword = 'Please Enter Confirm Password';
    } else if (formData.confirmpassword !== formData.password) {
      errors.confirmpassword = 'Confirm Password Should be Same as Password';
    }
    if (formData.premiumType === '0') {
      errors.premiumType = 'Please choose ace';
    }
    if (!formData.userCount) {
      errors.userCount = 'Please enter number of users';
    } else {
      // Get max users from selected plan dynamically from API
      const maxUsers = getMaxUsersFromPlan(formData.premiumType, true);
      if (maxUsers === null || maxUsers === undefined) {
        errors.userCount = 'Unable to validate user limit. Please refresh and try again.';
      } else if (parseInt(formData.userCount) > maxUsers) {
        errors.userCount = `Can't add more than ${maxUsers} users`;
      }
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    setError('');
    setSuccess('');
    
    // Calculate expected amount for logging
    const [durationType, planAmount] = formData.premiumType.split('_');
    const expectedAmount = (parseFloat(planAmount) * parseInt(formData.userCount)).toFixed(2);
    const selectedPlan = acePlans.find(plan => 
      plan.duration_type === parseInt(durationType) && 
      parseFloat(plan.amount) === parseFloat(planAmount)
    );
    
    console.log('=== Team Ace Registration Debug ===');
    console.log('Selected Plan:', selectedPlan);
    console.log('Premium Type:', formData.premiumType);
    console.log('Duration Type:', durationType);
    console.log('Plan Amount (per worker):', planAmount);
    console.log('Number of Users:', formData.userCount);
    console.log('Expected Total Amount:', `$${expectedAmount}`);
    console.log('Calculation:', `${planAmount} × ${formData.userCount} = ${expectedAmount}`);
    console.log('Request Payload:', {
      email: formData.email,
      premium_type_team_paid: formData.premiumType,
      number_of_user: formData.userCount,
      plan_type: 3, // Team Ace plan type
    });
    
    try {
      const response = await fetch(process.env.REACT_APP_API_URL+'/api/v1/team-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          premium_type_team_paid: formData.premiumType,
          number_of_user: formData.userCount,
          plan_type: 3, // Team Ace plan type (3 = Team Ace, 2 = Team Premium)
        }),
      });

      // Check if response is ok
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      console.log('=== Team Ace API Response ===');
      console.log('Full Response:', result);
      if (result.data) {
        console.log('Checkout ID:', result.data.checkout_id);
        console.log('Status:', result.data.status);
        // If backend returns amount info, log it
        if (result.data.amount) {
          console.log('Backend Calculated Amount:', result.data.amount);
          console.log('Expected Amount:', `$${expectedAmount}`);
          if (parseFloat(result.data.amount) !== parseFloat(expectedAmount)) {
            console.warn('⚠️ AMOUNT MISMATCH!');
            console.warn('Expected:', `$${expectedAmount}`);
            console.warn('Backend Calculated:', `$${result.data.amount}`);
          }
        }
      }
      
      // Handle new response format
      if (result.status && result.data) {
        if (result.data.status === 1 && result.data.checkout_id) {
          setSuccess(result.message || 'Registration successful! Redirecting to payment...');
          gotoCheckout(result.data.checkout_id);
        } else {
          setSuccess(result.message || 'Registration successful!');
        }
      } else {
        setError(result.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError(''+err.message||'');
      console.log(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Stripe checkout
  const gotoCheckout = (session_id) => { 
    console.log('gotoCheckout called with session_id:', session_id);
    
    const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';
    const stripeKey = isProduction 
      ? process.env.REACT_APP_STRIPE_LIVE_PUBLISHABLE_KEY 
      : process.env.REACT_APP_STRIPE_TEST_PUBLISHABLE_KEY;
    
    console.log('isProduction:', isProduction, 'stripeKey exists:', !!stripeKey);
    
    if (!stripeKey) {
      alert('Payment system not configured. Please contact support.');
      return;
    }
    
    const stripe = window.Stripe && window.Stripe(stripeKey);
    console.log('Stripe initialized:', !!stripe);
    
    if (stripe) {
      stripe.redirectToCheckout({ sessionId: session_id }).then(function (result) {
        if (result.error) {
          alert(result.error.message);
        }
      }).catch(function(error) {
        alert('Payment redirect failed. Please try again.');
        console.error('Stripe error:', error);
      });
    } else {
      alert('Payment system unavailable. Please refresh the page.');
    }
  };
 
  const premiumMonthlyPlan = plans.find(plan => plan.duration_type === 1);
  const premiumYearlyPlan = plans.find(plan => plan.duration_type === 2);
  const aceMonthlyPlan = acePlans.find(plan => plan.duration_type === 1);
  const aceYearlyPlan = acePlans.find(plan => plan.duration_type === 2);

  return (
    <div className="for-teams" style={{"display":"flex"}}>
      {/* Teams Premium Card */}
      <div className="pricing">
        <div className="pricing__content">
          <div className="pricing__header">
            <div className="pricing__title">
              <h1 className="pricing__h is--pro">Premium</h1>
            </div>
          </div>
          <div className="pricing__price">
            <h2 className="h2-2 is--price">
              {premiumMonthlyPlan ? `$${Number(premiumMonthlyPlan.amount).toFixed(2)}` : '$--'}
            </h2>
            <div className="price-yr">/worker/monthly</div>
            <div className="text-m is--price">
              <span className="is--dimmed">or </span>
              <span className="is--bold">
                {premiumYearlyPlan ? `$${Number(premiumYearlyPlan.amount).toFixed(2)}` : '$--'}
              </span>
              <span className="is--dimmed"> /worker/year</span>
            </div>
          </div>
          <div className="pricing__features">
            <div className="features-list">
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">Create a team for free</div>
              </div>
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">Unlimited trips</div>
              </div>
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">Super Administrator dashboard</div>
              </div>
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">Add and remove workers</div>
              </div>
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">Add and remove clients</div>
              </div>
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">IRS-compliant Data report</div>
              </div>
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">Custom rates</div>
              </div>
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">Single Billing</div>
              </div>
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">Premium support</div>
              </div>
            </div>
          </div>
          <div className="pricing__cta">
            <button
              type="button"
              id="getstart3"
              className="button is--pricing w-inline-block"
              onClick={handleOpenTeamPaidModal}
            >
              <div>Get started</div>
            </button>
          </div>
        </div>
      </div>

      {/* Teams Ace Card */}
      <div className="pricing">
        <div className="pricing__content">
          <div className="pricing__header">
            <div className="pricing__title">
              <h1 className="pricing__h is--ace">Ace</h1>
            </div>
          </div> 
          <div className="pricing__price">
            <h2 className="h2-2 is--price">
              {aceMonthlyPlan ? `$${Number(aceMonthlyPlan.amount).toFixed(2)}` : '$--'}
            </h2>
            <div className="price-yr">/worker/monthly</div>
            <div className="text-m is--price">
              <span className="is--dimmed">or </span>
              <span className="is--bold">
                {aceYearlyPlan ? `$${Number(aceYearlyPlan.amount).toFixed(2)}` : '$--'}
              </span>
              <span className="is--dimmed"> /worker/year</span>
            </div>
          </div>
          <div className="pricing__features">
            <div className="features-list">
              {(() => {
                const premiumFeatures = [
                  'Create a team for free',
                  'Unlimited trips',
                  'Super Administrator dashboard',
                  'Add and remove workers',
                  'Add and remove clients',
                  'IRS-compliant Data report',
                  'Custom rates',
                  'Single Billing',
                  'Premium support'
                ];
                
                return (
                  <div className="feature-item" style={{ alignItems: 'flex-start', marginBottom: '0rem' }}>
                    <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" style={{ marginTop: '2px' }} />
                    <div className="text-l is--dimmed" style={{ width: '100%', flex: 1 }}>
                      <div style={{ 
                        marginBottom: '0.875rem', 
                        fontWeight: '500',
                        color: '#2d3748',
                        fontSize: '15px'
                      }}>
                        All features of Teams Premium
                      </div>
                      <div style={{ 
                        marginTop: '0.625rem', 
                        padding: '0.875rem 1rem',
                        paddingLeft: '1.25rem',
                        background: 'rgba(74, 144, 226, 0.04)',
                        borderRadius: '6px',
                        borderLeft: '4px solid rgb(253 209 6)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                      }}>
                        {premiumFeatures.map((feature, index) => (
                          <div 
                            key={index}
                            style={{ 
                              fontSize: '14px', 
                              color: '#4a5568',
                              lineHeight: '1.5',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '0.5rem'
                            }}
                          >
                            <span style={{ color: '#4e0159', fontWeight: '600', marginRight: '2px' }}>✓</span>
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">Customized Rates</div>
              </div>
              <div className="feature-item">
                <img src="img/checkbox.svg" loading="lazy" alt="" className="feature-checkbox" />
                <div className="text-l is--dimmed">Dedicated support team</div>
              </div>
            </div>
          </div>
          <div className="pricing__cta">
            <button id="getstart4" className="button is--pricing w-inline-block" onClick={handleOpenTeamAceModal}>
              Get started
            </button>
          </div>


        </div>
      </div>

      {/* Contact Section at Bottom of Pricing */}
      <div className="pricing custom-quote-wrapper" style={{ marginTop: '24px', width: '100%',maxWidth:"45%" }}>
        <div className="pricing__content" style={{ width: '100%', maxWidth: '100%' }}>
          <div className="custom-quote-card" style={{ 
            position: 'relative', 
            overflow: 'hidden', 
            borderRadius: '16px', 
            border: '1.5px solid rgb(227,231,238)', 
            padding: '8px 28px', 
            background: 'linear-gradient(135deg, rgb(248,249,255) 0%, rgb(240,244,255) 100%)',
            boxShadow: 'rgba(78, 1, 89, 0.08) 0px 4px 20px',
            textAlign: 'center',
            maxWidth: '700px',
            margin: '0 auto',
            width: 'calc(100% - 0px)!important'
          }}>
            {/* Decorative circle (top-right) */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: -15,
                right: -15,
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(78,1,89,0.1) 0%, rgba(78,1,89,0.05) 100%)',
                zIndex: 0,
                pointerEvents: 'none'
              }}
            />

            <div className="custom-quote-content" style={{ position: 'relative', zIndex: 1 }}>
              {/* Icon */}
              <div className="custom-quote-icon"
                style={{
                  margin: '0 auto 4px',
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgb(78,1,89) 0%, rgb(108,0,122) 100%)',
                  boxShadow: 'rgba(78, 1, 89, 0.25) 0px 4px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 17L12 22L22 17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 12L12 17L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              {/* Heading */}
              <h3 className="custom-quote-heading" style={{ 
                margin: '0 0 0px 0',
                padding: 0,
                fontSize: '18px', 
                fontWeight: '600',
                color: 'rgb(55, 65, 81)',
                letterSpacing: '-0.01em'
              }}>
                Need a Custom Quote?
              </h3>

              {/* Description */}
              <p className="custom-quote-description" style={{ 
                margin: '0 auto 8px',
                padding: 0,
                color: 'rgb(107, 114, 128)',
                fontSize: '14px',
                lineHeight: '1.5',
                maxWidth: '550px'
              }}>
                For teams of more than 50 members, contact us for a custom quote 
              </p>

              {/* Email */}
              <div className="custom-quote-email" style={{ 
                marginBottom: '20px',
                padding: '8px 0'
              }}>
                <a
                  href="mailto:support@eworxs.app"
                  style={{ 
                    color: 'rgb(78,1,89)', 
                    textDecoration: 'none', 
                    fontSize: '15px',
                    fontWeight: '600',
                    transition: 'opacity 0.2s',
                    display: 'inline-block'
                  }}
                  onMouseOver={(e) => e.target.style.opacity = '0.8'}
                  onMouseOut={(e) => e.target.style.opacity = '1'}
                >
                  support@eworxs.app
                </a>
              </div>

              {/* Button */}
              <button
                type="button"
                className="custom-quote-button"
                onClick={handleOpenTeamContactModal}
                style={{
                  backgroundColor: 'rgb(78,1,89)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 28px',
                  fontSize: '14px',
                  fontWeight: '600',
                  minWidth: '180px',
                  boxShadow: 'rgba(78, 1, 89, 0.25) 0px 4px 12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  letterSpacing: '0.01em'
                }}
                onMouseOver={(e) => {
                  e.target.style.opacity = '0.9';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = 'rgba(78, 1, 89, 0.35) 0px 6px 16px';
                }}
                onMouseOut={(e) => {
                  e.target.style.opacity = '1';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'rgba(78, 1, 89, 0.25) 0px 4px 12px';
                }}
                aria-label="Request custom quote"
              >
                Request Custom Quote
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Teams Premium */}
      {showTeamPaidModal && (
        <div id="team-paid-popup3" className="modal" style={{ display: 'block' }} onClick={(e) => {
          if (e.target.id === 'team-paid-popup3') {
            handleCloseTeamPaidModal();
          }
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <span className="close" onClick={handleCloseTeamPaidModal}>&times;</span>
              <div className="modal-header">
                <h4>Register</h4>
              </div>
              <div className="modal-body">
                <div className="container-sec">
                  <form id="form_data_team_paid" method="post" onSubmit={handleTeamPaidSubmit}>
                    <div className="form-group">
                      <input type="text" placeholder="Email" name="email" id="email_team_paid" className="form-control" value={formData.email} onChange={handleModalInputChange} />
                      {formErrors.email && (
                        <div id="error_1_team_paid" className="text-danger">{formErrors.email}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <input type="password" placeholder="Password" name="password" id="password_team_paid" className="form-control type_password" value={formData.password} onChange={handleModalInputChange} />
                      {formErrors.password && (
                        <div id="error_2_team_paid" className="text-danger">{formErrors.password}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <input type="password" name="confirmpassword" id="confirmpassword_team_paid" className="form-control type_password" placeholder="Confirm Password" value={formData.confirmpassword} onChange={handleModalInputChange} />
                      {formErrors.confirmpassword && (
                        <div id="error_4_team_paid" className="text-danger">{formErrors.confirmpassword}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <select
                        name="premiumType"
                        id="premium_type_team_paid"
                        className="form-control"
                        value={formData.premiumType}
                        onChange={handleModalInputChange}
                      >
                        <option value="0">Please choose premium type</option>
                        {plans && Array.isArray(plans) && plans.map(plan => (
                          <option
                            key={plan.id}
                            value={`${plan.duration_type}_${plan.amount}`}
                          >
                            ${plan.amount} /{plan.duration_type === 1 ? 'Monthly' : 'Yearly'}
                          </option>
                        ))}
                      </select>
                      {formErrors.premiumType && (
                        <div id="error_3_team_paid_premium" className="text-danger">{formErrors.premiumType}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <input type="text" placeholder="Number of Workers" name="userCount" id="number_of_user_team_paid" className="form-control" value={formData.userCount} onChange={handleModalInputChange} />
                      {(formErrors.userCount || amountError) && (
                        <div id="error_3_team_paid" className="text-danger">{formErrors.userCount || amountError}</div>
                      )}
                    </div>
                    {amount && (
                      <div className="form-group">
                        <label htmlFor="" id="number_of_user_Amount_team_paid" className="label-input">{amount}</label>
                      </div>
                    )}
                    <div className="form-group">
                      <button type="submit" id="submit_business_paid" className="button is--pricing w-inline-block" disabled={loading}>
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                      <div id="overlay3" style={{ display: loading ? 'block' : 'none' }}>
                        <img src="loading.gif" loading="lazy" alt="Loading" width="40" />
                        Loading...
                      </div>
                    </div>
                    <div className="form-group">
                      <a className="btn btn-success btn-block" style={{ color: 'white', display: 'none' }} id="generate_fees_team_paid">
                        Generate Fees
                      </a>
                      {success && (
                        <div id="success_team_paid" className="text-success" style={{ padding: '15px' }}>{success}</div>
                      )}
                      {error && (
                        <div className="text-danger">{error}</div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Teams Ace */}
      {showTeamAceModal && (
        <div id="team-ace-popup3" className="modal" style={{ display: 'block' }} onClick={(e) => {
          if (e.target.id === 'team-ace-popup3') {
            handleCloseTeamAceModal();
          }
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <span className="close" onClick={handleCloseTeamAceModal}>&times;</span>
              <div className="modal-header">
                <h4>Register</h4>
              </div>
              <div className="modal-body">
                <div className="container-sec">
                  <form id="form_data_team_ace_paid" method="post" onSubmit={handleTeamAceSubmit}>
                    <div className="form-group">
                      <input type="text" placeholder="Email" name="email" id="email_team_paid" className="form-control" value={formData.email} onChange={handleModalInputChange} />
                      {formErrors.email && (
                        <div id="error_1_team_ace_paid" className="text-danger">{formErrors.email}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <input type="password" placeholder="Password" name="password" id="password_team_paid" className="form-control type_password" value={formData.password} onChange={handleModalInputChange} />
                      {formErrors.password && (
                        <div id="error_2_team_ace_paid" className="text-danger">{formErrors.password}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <input type="password" name="confirmpassword" id="confirmpassword_team_paid" className="form-control type_password" placeholder="Confirm Password" value={formData.confirmpassword} onChange={handleModalInputChange} />
                      {formErrors.confirmpassword && (
                        <div id="error_4_team_ace_paid" className="text-danger">{formErrors.confirmpassword}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <select
                        name="premiumType"
                        id="premium_type_team_ace_paid"
                        className="form-control"
                        value={formData.premiumType}
                        onChange={handleModalInputChange}
                      >
                        <option value="0">Please choose ace type</option>
                        {acePlans && Array.isArray(acePlans) && acePlans.map(plan => (
                          <option
                            key={plan.id}
                            value={`${plan.duration_type}_${plan.amount}`}
                          >
                            ${plan.amount} /{plan.duration_type === 1 ? 'Monthly' : 'Yearly'}
                          </option>
                        ))}
                      </select>
                      {formErrors.premiumType && (
                        <div id="error_3_team_ace_premium" className="text-danger">{formErrors.premiumType}</div>
                      )}
                    </div>
                    <div className="form-group">
                      <input type="text" placeholder="Number of Workers" name="userCount" id="number_of_user" className="form-control" value={formData.userCount} onChange={handleModalInputChange} />
                      {(formErrors.userCount || amountError) && (
                        <div id="error_3_team_ace_paid" className="text-danger">{formErrors.userCount || amountError}</div>
                      )}
                    </div>
                    {amount && (
                      <div className="form-group">
                        <label htmlFor="" id="number_of_user_Amount" className="label-input">{amount}</label>
                      </div>
                    )}
                    <div className="form-group">
                      <button type="submit" id="submit_business_paid" className="button is--pricing w-inline-block" disabled={loading}>
                        {loading ? 'Saving...' : 'Save'}
                      </button>
                      <div id="overlay3" style={{ display: loading ? 'block' : 'none' }}>
                        <img src="loading.gif" loading="lazy" alt="Loading" width="40" />
                        Loading...
                      </div>
                    </div>
                    <div className="form-group">
                      <a className="btn btn-success btn-block" style={{ color: 'white', display: 'none' }} id="generate_fees">
                        Generate Fees
                      </a>
                      {success && (
                        <div id="success_team_paid" className="text-success" style={{ padding: '15px' }}>{success}</div>
                      )}
                      {error && (
                        <div className="text-danger">{error}</div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}





      {/* Contact Modal for Teams Ace */}
      {showTeamContactModal && (
        <div id="contact-popup3" className="modal" style={{ display: 'block' }} onClick={(e) => {
          if (e.target.id === 'contact-popup3') {
            handleCloseTeamContactModal();
          }
        }}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <span className="close" onClick={handleCloseTeamContactModal}>&times;</span>
              <div className="modal-header">
                <h4>Register</h4>
              </div>
              <div className="modal-body">
                <div className="container-sec">
                  <form id="form_data_contact" onSubmit={handleSubmit}>
                    <div className="form-group">
                      <input 
                        type="text" 
                        placeholder="Email" 
                        name="email" 
                        id="email_team_contact" 
                        className="form-control"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <textarea 
                        name="message" 
                        placeholder="Message" 
                        id="Message_contact" 
                        cols="30" 
                        rows="5"
                        className="form-control"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <button 
                        type="submit" 
                        id="submit_email" 
                        className="btn btn-primary btn-block button-form submitemailbtn" 
                        disabled={loading}
                      >
                        Save
                      </button>
                    </div>
                    {loading && (
                      <div id="overlay4" className="modal-loading">
                        <img src="loading.gif" alt="Loading" loading="lazy" width="40" />
                        Loading...
                      </div>
                    )}
                    {error && (
                      <div id="error_1_team_contact" className="text-danger">
                        {error}
                      </div>
                    )}
                    {success && (
                      <div id="success_team_contact" className="text-success" style={{ padding: '15px' }}>
                        {success}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPart; 